import { Component, ChangeDetectionStrategy, ChangeDetectorRef } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from "@ngx-translate/core";
import { SaysoVerifySnippetService } from "@onsip/common/services/api/resources/saysoVerifySnippetService/sayso-verify-snippet.service";

interface ApiURLErrorResponse {
  success: boolean;
  errorMessage: string;
}

@Component({
  selector: "onsip-test-installation",
  templateUrl: "./test-installation.component.html",
  styleUrls: ["./test-installation.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TestInstallationComponent {
  form: FormControl = new FormControl("", [
    // regex: Whitelist all possible URI chars per: https://tools.ietf.org/html/rfc3986
    Validators.pattern("^[A-Za-z0-9-._~:?#@!$&'*+,;=%/\\[\\]\\(\\)]+$")
  ]);

  /** Return true for errors that are useful to display to user, false for unexpected/other errs
   *  Because the SaysoVierify Snippet API call can contain some useful distinct error msgs
   */
  private static handleApiErrors(err: Error): string {
    // mini reverse lookup table so we can provide the consumer
    // with the i18n key rather than the english text from the API error.
    const i18nReverseLookup: Record<string, string> = {
      'Missing "window.onsip" declaration!': "ONSIP_I18N.MISSING_WINDOWONSIP_DECLARATION",
      'Missing "window.onsip.do" declaration!': "ONSIP_I18N.MISSING_WINDOWONSIPDO_DECLARATION",
      'Missing "onsip.js" script tag!': "ONSIP_I18N.MISSING_ONSIPJS_SCRIPT_TAG",
      'Missing your organization\'s "onsip.do" initialization!':
        "ONSIP_I18N.MISSING_YOUR_ORGANIZATIONS_ONSIPDO_INITIALIZATION",
      "Request failed with status code 404!": "ONSIP_I18N.REQUEST_FAILED_WITH_STATUS_CODE_404"
    };
    if (err && err.message) {
      for (const msg of Object.keys(i18nReverseLookup)) {
        // Whitelist error messages we may want to display to user
        if (err.message.includes(msg)) return i18nReverseLookup[msg];
      }
    }
    return "ONSIP_I18N.INVALID_URL";
  }

  constructor(
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private saysoVerifySnippetService: SaysoVerifySnippetService,
    private cdRef: ChangeDetectorRef
  ) {}

  getErrorMessage(): string {
    const translateKey = this.form.hasError("httpsFormat")
      ? "ONSIP_I18N.MUST_BEGIN_WITH_HTTPS"
      : this.form.hasError("pattern")
      ? "ONSIP_I18N.FIELD_CONTAINS_ONE_OR_MORE_INVALID_CHARACTERS"
      : this.form.hasError("apiError") && this.form.errors
      ? this.form.errors.apiError
      : "";
    return translateKey.length > 0 ? this.translate.instant(translateKey) || "" : "";
  }

  openSnackBar(msg: string) {
    this.snackBar.open(msg, "", {
      duration: 1500
    });
  }

  /** Remove apiError on input change and set url in service */
  resetTest(): void {
    this.form.hasError("apiError") &&
      !this.form.hasError("httpsFormat") &&
      // eslint-disable-next-line no-null/no-null
      this.form.setErrors(null);
  }

  /** On Test Connection click: check url and inform user of result with snackbar and mat-error */

  testUrl(): void {
    // regex explain:
    // https://test.com -> https://test.com
    // http://test.com ->  https://test.com (will fail)
    // test.com ->         https://test.com
    const url = this.form.value.replace(
      /(^https?:\/\/(.*)|^(.*))/,
      (a: string, b: string, c: string, d: string) => "https://" + (c || d)
    );
    const response = { success: true, errorMessage: "" } as ApiURLErrorResponse;
    this.saysoVerifySnippetService.saysoVerifySnippet({ Url: url }).then(res => {
      if (res.status === "error") {
        response.success = false;
        response.errorMessage = TestInstallationComponent.handleApiErrors({
          name: res.data.code,
          message: res.data.message
        });
        if (response.errorMessage.length === 0) throw new Error(res.data.message); // not a whitelisted error
        this.openSnackBar(this.translate.instant(response.errorMessage));
        this.form.setErrors({ apiError: response.errorMessage }); // set form error
      } else if (res.status === "success") {
        this.openSnackBar(
          this.translate.instant("ONSIP_I18N.SUCCESS_SAYSO_IS_SET_UP_ON_YOUR_SITE")
        );
      }
      this.cdRef.markForCheck();
    });
  }
}
